/*
 * DLUX hero slideshow
 * 2026-08-01: autoplay reworked for WCAG 2.2.2 (Pause, Stop, Hide, Level A).
 *   - visible pause/play control
 *   - autoplay stops permanently on first user interaction (Baymard)
 *   - no autoplay on mobile or for prefers-reduced-motion users
 *   - inactive slides are made inert as a defensive measure
 */
(function () {
  var MOBILE_QUERY = '(max-width: 749px)';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

  function mq(query) {
    return !!(window.matchMedia && window.matchMedia(query).matches);
  }

  /*
   * Reduced motion always wins: those visitors never get auto-rotation.
   * Mobile auto-rotation is a merchant setting. Baymard advises against it,
   * but with only two slides and most traffic on mobile, turning it off means
   * most visitors never see slide two at all. When it is on, the visible pause
   * control below is what keeps this compliant with WCAG 2.2.2.
   */
  function autoplayAllowed(mobileAllowed) {
    if (mq(REDUCED_MOTION_QUERY)) return false;
    if (mq(MOBILE_QUERY)) return mobileAllowed;
    return true;
  }

  function init(root) {
    if (root.getAttribute('data-dlux-hero-ready') === 'true') return;
    root.setAttribute('data-dlux-hero-ready', 'true');

    var slides = root.querySelectorAll('[data-dlux-hero-slide]');
    if (!slides.length) return;

    var dotsWrap = root.querySelector('[data-dlux-hero-dots]');
    var prevBtn = root.querySelector('[data-dlux-hero-prev]');
    var nextBtn = root.querySelector('[data-dlux-hero-next]');
    var toggleBtn = root.querySelector('[data-dlux-hero-toggle]');

    var intervalMs = parseInt(root.getAttribute('data-interval'), 10) || 6000;
    var autoplayRequested = root.getAttribute('data-autoplay') === 'true' && slides.length > 1;
    var mobileAllowed = root.getAttribute('data-autoplay-mobile') === 'true';

    var canAutoplay = autoplayRequested && autoplayAllowed(mobileAllowed);
    var userStopped = false;
    var paused = false;
    var index = 0;
    var timer = null;

    var supportsInert = 'inert' in HTMLElement.prototype;

    function setInteractive(slide, on) {
      if (supportsInert) {
        slide.inert = !on;
        return;
      }
      var nodes = slide.querySelectorAll(FOCUSABLE);
      for (var f = 0; f < nodes.length; f++) {
        if (on) nodes[f].removeAttribute('tabindex');
        else nodes[f].setAttribute('tabindex', '-1');
      }
    }

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      for (var d = 0; d < slides.length; d++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'dlux-hero__dot';
        b.setAttribute('data-index', String(d));
        b.setAttribute('aria-label', 'Go to slide ' + (d + 1));
        dotsWrap.appendChild(b);
      }
    }

    function syncDots() {
      if (!dotsWrap) return;
      var dots = dotsWrap.querySelectorAll('.dlux-hero__dot');
      for (var j = 0; j < dots.length; j++) {
        dots[j].classList.toggle('is-active', j === index);
        dots[j].setAttribute('aria-current', j === index ? 'true' : 'false');
      }
    }

    function show(n) {
      var len = slides.length;
      index = ((n % len) + len) % len;
      for (var j = 0; j < slides.length; j++) {
        var on = j === index;
        slides[j].classList.toggle('is-active', on);
        slides[j].setAttribute('aria-hidden', on ? 'false' : 'true');
        setInteractive(slides[j], on);
      }
      syncDots();
    }

    function next() {
      show(index + 1);
    }
    function prev() {
      show(index - 1);
    }

    function clearTimer() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function startTimer() {
      if (!canAutoplay || userStopped || paused) return;
      clearTimer();
      timer = window.setInterval(next, intervalMs);
    }

    function syncToggle() {
      if (!toggleBtn) return;
      // Nothing to pause when autoplay never runs, so remove the control entirely.
      if (!canAutoplay || userStopped) {
        toggleBtn.hidden = true;
        return;
      }
      toggleBtn.hidden = false;
      toggleBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      toggleBtn.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      toggleBtn.classList.toggle('is-paused', paused);
    }

    // Any manual navigation ends auto-rotation for the rest of the visit.
    function stopForGood() {
      userStopped = true;
      clearTimer();
      syncToggle();
    }

    buildDots();
    show(0);
    syncToggle();

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        stopForGood();
        prev();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        stopForGood();
        next();
      });
    }

    if (dotsWrap) {
      dotsWrap.addEventListener('click', function (e) {
        var t = e.target && e.target.closest ? e.target.closest('button[data-index]') : null;
        if (!t || !dotsWrap.contains(t)) return;
        stopForGood();
        show(parseInt(t.getAttribute('data-index'), 10));
      });
    }

    if (toggleBtn) {
      toggleBtn.addEventListener('click', function () {
        paused = !paused;
        if (paused) clearTimer();
        else startTimer();
        syncToggle();
      });
    }

    // Desktop hover and keyboard focus still pause, but only while autoplay is alive.
    root.addEventListener('mouseenter', clearTimer);
    root.addEventListener('mouseleave', startTimer);
    root.addEventListener('focusin', clearTimer);
    root.addEventListener('focusout', startTimer);

    // Do not keep rotating in a background tab.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) clearTimer();
      else startTimer();
    });

    // Re-evaluate if the viewport crosses the mobile breakpoint or the user
    // changes their reduced-motion preference mid-session.
    function reevaluate() {
      var allowed = autoplayRequested && autoplayAllowed(mobileAllowed);
      if (allowed === canAutoplay) return;
      canAutoplay = allowed;
      if (canAutoplay) startTimer();
      else clearTimer();
      syncToggle();
    }

    if (window.matchMedia) {
      var mobileMql = window.matchMedia(MOBILE_QUERY);
      var motionMql = window.matchMedia(REDUCED_MOTION_QUERY);
      if (mobileMql.addEventListener) {
        mobileMql.addEventListener('change', reevaluate);
        motionMql.addEventListener('change', reevaluate);
      } else if (mobileMql.addListener) {
        mobileMql.addListener(reevaluate);
        motionMql.addListener(reevaluate);
      }
    }

    startTimer();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-dlux-hero]').forEach(init);
  }

  initAll(document);

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
